/**
 * GroupCover.jsx
 * ─────────────────────────────────────────────
 * New entry-point flow for the Digital Billions Group site.
 *
 * Sequence 1 — a centered, animated "Digital Billions Group" wordmark
 *              holds for ~3s, then fades out.
 * Sequence 2 — a smaller Group header docks at the top and a 3-tile
 *              carousel reveals below it. The selected tile sits up
 *              front (glowing); the other two peek in from behind at
 *              the sides (vignetted, dimmed). Clicking a side tile
 *              rotates the set — the clicked tile animates to center.
 *              Clicking the front tile follows through to that project.
 *
 * Tiles:
 *   Main Site — internal route to the existing marketing homepage (/home)
 *   APS       — external link to advanced.digitalbillionsgroup.com
 *   Vending   — external link to vendbabee.com
 *
 * Reuses the site's existing icy-grey design tokens and ambient
 * components (NoiseTexture, ParticleField, VignetteOverlay, GlowLine)
 * so the gateway feels native to the rest of the app.
 */
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import NoiseTexture from "../design-tools/NoiseTexture";
import ParticleField from "../design-tools/ParticleField";
import VignetteOverlay from "../design-tools/VignetteOverlay";
import GlowLine from "../design-tools/GlowLine";
import { PALETTE } from "../design-tools/tokens";
import heroVideo from "../assets/dgb-hero-compressed.mp4";
import apsVideo from "../assets/aps.mp4";
import vendingVideo from "../assets/vendbabee.mp4";
import bgImage from "../assets/Seedream-4_00012_.png";
import "./GroupCover.css";

const INTRO_HOLD_MS = 3000;
const INTRO_EXIT_MS = 800;

const TILES = [
  {
    id: "main",
    label: "Digital Billions Group",
    name: "Digital Billions Group",
    kicker: "◈ Flagship Studio",
    description:
      "Our flagship studio — distinguished development, automation, and design for ambitious brands worldwide.",
    kind: "internal",
    href: "/home",
    video: heroVideo,
  },
  {
    id: "aps",
    label: "Advanced Platform Services",
    name: "Advanced Platform Services",
    kicker: "◈ Enterprise Infrastructure",
    description:
      "Advanced Platform Services - The division of our team that delivers compliance-driven infrastructure for organizations where uptime, security, and scale are non-negotiable.",
    kind: "external",
    href: "https://advanced.digitalbillionsgroup.com",
    video: apsVideo,
  },
  {
    id: "vending",
    label: "VendBabee",
    name: "Vendbabee",
    kicker: "◈ Digital Vending",
    description:
      "Modern AI Smart Vending / VendBabee transforms everyday convenience with intelligent smart vending solutions that deliver seamless, cashless access to snacks, beverages, and essentials 24/7.",
    kind: "external",
    href: "https://vendbabee.com",
    video: vendingVideo,
  },
];

/* ─────────────────────────────────────────────
   Tile art — each project gets its own looping
   background video
───────────────────────────────────────────── */
function TileArt({ tile }) {
  return (
    <div className="gc-tile-art">
      <video src={tile.video} autoPlay loop muted playsInline />
      <div className="gc-tile-art-overlay" />
    </div>
  );
}

/* ─────────────────────────────────────────────
   A single tile — always the same element type
   (Link vs external <a>, fixed per tile) so the
   rotation transition never snaps/remounts.
───────────────────────────────────────────── */
function Tile({ tile, index, activeIndex, onSelect }) {
  const total = TILES.length;
  const rel = (index - activeIndex + total) % total;
  const isActive = rel === 0;
  const slotClass = isActive
    ? "gc-tile--active"
    : rel === 1
      ? "gc-tile--right"
      : "gc-tile--left";

  const handleClick = (e) => {
    if (!isActive) {
      e.preventDefault();
      onSelect(index);
    }
  };

  const content = (
    <>
      <TileArt tile={tile} />
      <div className="gc-tile-body">
        <div className="gc-tile-kicker">{tile.kicker}</div>
        <div className="gc-tile-title">{tile.label}</div>
      </div>
    </>
  );

  const shared = {
    className: `gc-tile ${slotClass}`,
    onClick: handleClick,
    "aria-current": isActive ? "true" : undefined,
    "aria-label": isActive
      ? `Open ${tile.label} — ${tile.name}`
      : `Bring ${tile.label} to the front`,
  };

  if (tile.kind === "internal") {
    return (
      <Link to={tile.href} {...shared}>
        {content}
      </Link>
    );
  }

  return (
    <a href={tile.href} target="_blank" rel="noopener noreferrer" {...shared}>
      {content}
    </a>
  );
}

/* ─────────────────────────────────────────────
   GroupCover
───────────────────────────────────────────── */
export default function GroupCover() {
  const [phase, setPhase] = useState("intro"); // intro | exiting | tiles
  const [activeIndex, setActiveIndex] = useState(0);
  const touchStartRef = useRef(null);

  const total = TILES.length;
  const goNext = () => setActiveIndex((i) => (i + 1) % total);
  const goPrev = () => setActiveIndex((i) => (i - 1 + total) % total);

  // Mobile swipe support — the click-to-select interaction that works
  // well with a mouse doesn't map intuitively to touch (tapping a
  // dimmed side tile to bring it forward isn't a discoverable gesture
  // on a phone). Instead, on touch devices a swipe advances the
  // carousel in the direction of the swipe. We check both axes because
  // below ~700px the stage re-axises to a vertical stack (see
  // GroupCover.css), so "forward" can read as either a horizontal or
  // vertical gesture depending on viewport — swiping toward where the
  // next tile visually peeks in should always work.
  const SWIPE_THRESHOLD = 40; // px — below this, treat as a tap, not a swipe

  const handleStageTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };

  const handleStageTouchEnd = (e) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;

    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);

    if (Math.max(absX, absY) < SWIPE_THRESHOLD) return; // tap, not a swipe — let onClick handle it

    if (absX > absY) {
      // Horizontal swipe: swipe left reveals the next tile (like flipping
      // a page forward), swipe right goes back.
      if (dx < 0) goNext();
      else goPrev();
    } else {
      // Vertical swipe (stacked mobile layout): swipe up brings the tile
      // peeking in from below forward, swipe down brings the one above.
      if (dy < 0) goNext();
      else goPrev();
    }
  };

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("exiting"), INTRO_HOLD_MS);
    const t2 = setTimeout(
      () => setPhase("tiles"),
      INTRO_HOLD_MS + INTRO_EXIT_MS,
    );
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  const skipIntro = () => {
    if (phase === "intro") setPhase("exiting");
  };

  const active = TILES[activeIndex];

  return (
    <div className="gc-root">
      <div
        className="gc-bg"
        aria-hidden="true"
        style={{ backgroundImage: `url(${bgImage})` }}
      />
      <NoiseTexture opacity={0.03} />

      {/* ── Sequence 1: animated wordmark ── */}
      {(phase === "intro" || phase === "exiting") && (
        <div
          className={`gc-intro${phase === "exiting" ? " gc-intro--out" : ""}`}
          onClick={skipIntro}
          role="button"
          tabIndex={0}
          aria-label="Skip intro"
          onKeyDown={(e) =>
            (e.key === "Enter" || e.key === " ") && skipIntro()
          }>
          <ParticleField style={{ opacity: 0.5 }} />
          <VignetteOverlay strength={0.55} color={PALETTE.ink} />

          <div
            className="gc-intro-kicker"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              letterSpacing: "0.4em",
              textTransform: "uppercase",
              color: PALETTE.teal500,
            }}>
            ◈ Est. MMXXII
          </div>

          <h1
            className="gc-intro-title"
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 300,
              fontSize: "clamp(32px, 9vw, 90px)",
              letterSpacing: "-0.02em",
              color: PALETTE.white,
              lineHeight: 1.05,
            }}>
            <span className="gc-intro-word">DIGITAL&nbsp; </span>
            <span className="gc-intro-word">BILLIONS&nbsp;</span>
            <span
              className="gc-intro-word"
              style={{
                fontWeight: 700,
                color: PALETTE.teal400,
                textShadow: "0 0 24px rgba(127,161,194,0.65)",
              }}>
              GROUP
            </span>
          </h1>

          <div className="gc-intro-bar-track">
            <div className="gc-intro-bar-fill" />
          </div>

          <div
            className="gc-intro-skip"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              letterSpacing: "0.3em",
              textTransform: "uppercase",
              color: "rgba(240,241,244,0.3)",
            }}>
            Tap to skip
          </div>
        </div>
      )}

      {/* ── Sequence 2: docked header + tile carousel ── */}
      {phase !== "intro" && (
        <div className={`gc-tiles${phase === "tiles" ? " gc-tiles--in" : ""}`}>
          <ParticleField style={{ opacity: 0.25 }} />
          <VignetteOverlay strength={0.4} color={PALETTE.ink} />

          <div className="gc-header">
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9,
                letterSpacing: "0.35em",
                textTransform: "uppercase",
                color: PALETTE.teal500,
                marginBottom: 16,
              }}>
              ◈ Digital Billions Group ◈
            </div>
            <GlowLine style={{ margin: "0 auto", maxWidth: 60 }} />
          </div>

          <div
            className="gc-stage"
            role="group"
            aria-label="Digital Billions Group projects"
            onTouchStart={handleStageTouchStart}
            onTouchEnd={handleStageTouchEnd}>
            {TILES.map((tile, i) => (
              <Tile
                key={tile.id}
                tile={tile}
                index={i}
                activeIndex={activeIndex}
                onSelect={setActiveIndex}
              />
            ))}
          </div>

          <div className="gc-shadow" aria-hidden="true" />

          <div className="gc-desc-wrap">
            <p className="gc-desc" key={active.id}>
              {active.description}
            </p>
          </div>

          <div className="gc-hint">Tap or swipe to bring a project forward</div>
        </div>
      )}
    </div>
  );
}
